import { DataStatus, isIdle } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import * as CreativesAPI from '../api/creativesAPI';
import type { CreativeItem, MediaDimensionValue } from './types';

export const mediaTupleKey = (v: MediaDimensionValue): string =>
  `${v.ad_id}|${v.media_source}`;

type FetchCreativesParams = MediaDimensionValue[];

interface State {
  authStatus: DataStatus;
  items: Record<string, CreativeItem>;
  missingKeys: Record<string, true>;
}

// Structural slice-state type used in condition callbacks to avoid circular RootState dependency.
interface StateShape {
  creatives?: State;
}

const initialState: State = {
  authStatus: DataStatus.idle,
  items: {},
  missingKeys: {},
};

export const creativesSlice = createAppSlice({
  name: 'creatives',
  initialState,
  reducers: (create) => ({
    initCreativesAuth: create.asyncThunk(
      (_: void) => CreativesAPI.authCreatives(),
      {
        pending: (state) => {
          state.authStatus = DataStatus.loading;
        },
        fulfilled: (state) => {
          state.authStatus = DataStatus.finished;
        },
        rejected: (state) => {
          state.authStatus = DataStatus.error;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as StateShape;
            return isIdle(state.creatives?.authStatus ?? DataStatus.idle);
          },
        },
      },
    ),
    fetchCreatives: create.asyncThunk(
      (params: FetchCreativesParams) => CreativesAPI.searchCreatives(params),
      {
        fulfilled: (state, action) => {
          const returnedKeys = new Set<string>();
          for (const item of action.payload) {
            const key = mediaTupleKey({
              ad_id: item.adId,
              media_source: item.sourceName,
            });
            state.items[key] = item;
            returnedKeys.add(key);
          }
          for (const param of action.meta.arg) {
            const key = mediaTupleKey(param);
            if (!returnedKeys.has(key)) {
              state.missingKeys[key] = true;
            }
          }
        },
        options: {
          condition: (params: FetchCreativesParams, { getState }) => {
            const state = getState() as StateShape;
            return params.some((v) => {
              const key = mediaTupleKey(v);

              return (
                !state.creatives?.items[key] &&
                !state.creatives?.missingKeys[key]
              );
            });
          },
        },
      },
    ),
  }),
  selectors: {
    selectCreativesAuthStatus: (state) => state.authStatus,
    selectCreativesAuthLoaded: (state) =>
      state.authStatus === DataStatus.finished,
    selectCreativesMap: (state) => state.items,
    selectIsCreativeMissing: (state, key: string) =>
      state.missingKeys[key] === true,
  },
});

export const { initCreativesAuth, fetchCreatives } = creativesSlice.actions;
export const {
  selectCreativesAuthStatus,
  selectCreativesAuthLoaded,
  selectCreativesMap,
  selectIsCreativeMissing,
} = creativesSlice.selectors;

export const selectCreativeByTuple =
  (value: MediaDimensionValue) =>
  (state: StateShape): CreativeItem | undefined =>
    state.creatives?.items[mediaTupleKey(value)];
